import {
    Scene,
    PerspectiveCamera,
    WebGLRenderer,
    Clock,
    AnimationMixer,
    Object3D,
    Vector3,
    Box3,
    Matrix4,
    MathUtils,
    EventDispatcher,
    LinearToneMapping,
    Color,
    PCFSoftShadowMap,
    Group,
    AmbientLight,
    DirectionalLight,
} from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import { iApp } from '@/Interfaces/app';

// Эмуляция поведения Verge3D App
export class ThreeApp extends EventDispatcher implements iApp {
    scene: Scene;
    camera: PerspectiveCamera;
    renderer: WebGLRenderer;
    controls: any; // OrbitControls + расширения
    clock = true;
    _clock: Clock = new Clock();
    container: HTMLElement;
    loader: GLTFLoader;
    mixer: AnimationMixer;
    actions: Array<any> = [];
    renderCallbacks: Array<any> = [];
    compileCallbacks: Array<any> = [];
    enableRender = true;
    disableRenderTrigger = 0;
    frame = 0;
    elapsed = 0;
    frameRateDivider = 1;
    aaMethod = 'MSAA';
    clearBkgOnLoad = false;
    ssaaOnPause = false;
    stats: any = null;
    useHDR = false;
    worldCubemapRes = 256;
    xrCameraParent: any = null;
    xrControllers: Array<any> = [];
    xrSession: any = null;

    private _renderRequested = false;

    constructor(containerId: string, settings: any = {}) {
        super();
        this.container = document.getElementById(containerId) as HTMLElement;
        this.scene = new Scene();
        this.camera = new PerspectiveCamera(45, this.getWidth() / this.getHeight(), 0.0001, 100);
        
        this.renderer = new WebGLRenderer({
            antialias: true,
            alpha: settings.alpha || false,
            preserveDrawingBuffer: settings.preserveDrawingBuffer || false,
            logarithmicDepthBuffer: true,
        });
        this.renderer.setPixelRatio(window.devicePixelRatio);
        this.renderer.setSize(this.getWidth(), this.getHeight());
        this.renderer.shadowMap.enabled = true;
        this.renderer.shadowMap.type = PCFSoftShadowMap;
        this.renderer.toneMapping = LinearToneMapping;
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        this.renderer.outputColorSpace = 'srgb';
        
        this.container.appendChild(this.renderer.domElement);

        this.loader = new GLTFLoader();
        this.mixer = new AnimationMixer(this.scene);

        // Добавляем базовое освещение, так как в Three.js оно не создается автоматически
        const ambientLight = new AmbientLight(0xffffff, 0.7);
        this.scene.add(ambientLight);

        const directionalLight = new DirectionalLight(0xffffff, 0.8);
        directionalLight.position.set(5, 10, 7.5);
        directionalLight.castShadow = true;
        this.scene.add(directionalLight);

        this.controls = new OrbitControls(this.camera, this.renderer.domElement);
        this.setupControlsExtensions();

        window.addEventListener('resize', () => this.onResize());
    }

    private setupControlsExtensions() {
        // Добавляем метод tween, который ожидает V3dTools
        this.controls.tween = (worldPos: any, worldTarget: any, duration: number, doSlot: () => void) => {
            if (duration <= 0) {
                if (worldPos) this.camera.position.copy(worldPos);
                if (worldTarget) this.controls.target.copy(worldTarget);
                this.controls.update();
                if (doSlot) doSlot();
                return;
            }

            // Simple "instant" move for now, but update position and target
            // In a real app we'd use TWEEN.js here
            if (worldPos) this.camera.position.copy(worldPos);
            if (worldTarget) this.controls.target.copy(worldTarget);
            this.controls.update();
            
            console.log('Camera moved to', worldPos, 'looking at', worldTarget);
            if (doSlot) doSlot();
        };
        this.controls.inTween = false;
    }

    getWidth() {
        return this.container.clientWidth;
    }

    getHeight() {
        return this.container.clientHeight;
    }

    onResize() {
        this.camera.aspect = this.getWidth() / this.getHeight();
        this.camera.updateProjectionMatrix();
        this.renderer.setSize(this.getWidth(), this.getHeight());
        this.render();
    }

    render() {
        if (!this.enableRender) return;
        this.renderer.render(this.scene, this.camera);
        this.renderCallbacks.forEach(cb => cb());
    }

    animate() {
        requestAnimationFrame(() => this.animate());
        const delta = this._clock.getDelta();
        this.elapsed += delta;
        this.frame++;
        
        if (this.mixer) {
            this.mixer.update(delta);
        }

        this.render();
    }

    run() {
        this.animate();
    }

    loadScene(url: string, loadCb: (loadedScene: Scene) => void, progressCb?: (percentage: number) => void, errorCb?: () => void) {
        this.loader.load(url, (gltf) => {
            this.scene.add(gltf.scene);
            if (loadCb) loadCb(this.scene);
            this.dispatchEvent({ type: 'sceneLoad' });
        }, (xhr) => {
            if (progressCb) progressCb((xhr.loaded / xhr.total) * 100);
        }, (error) => {
            console.error('Error loading scene:', error);
            if (errorCb) errorCb();
        });
    }

    appendScene(url: string, afterLoadCb: (scene: Scene) => void, onprogress?: (percentage: number) => void, errorCb?: (error: any) => void) {
        this.loader.load(url, (gltf) => {
            this.scene.add(gltf.scene);
            if (afterLoadCb) afterLoadCb(gltf.scene);
        }, (xhr) => {
            if (onprogress) onprogress((xhr.loaded / xhr.total) * 100);
        }, (error) => {
            console.error('Error appending scene:', error);
            if (errorCb) errorCb(error);
        });
    }

    enableControls(element?: HTMLElement) {
        this.controls.enabled = true;
    }

    disableRendering(after: number) {
        if (after === 0) {
            this.enableRender = false;
        } else {
            setTimeout(() => {
                this.enableRender = false;
            }, after * 1000);
        }
    }

    enableRendering() {
        this.enableRender = true;
    }

    hideFPS() {}
    showFPS() { return undefined; }

    getCamera() {
        return this.camera;
    }

    setCamera(camera: any) {
        this.camera = camera;
        this.controls.object = camera;
        return this.camera;
    }

    enableSSAA(sampleLevel: number, iterative: boolean) {
        // В чистом Three.js SSAA требует доп. проходов, пока пропустим
        console.warn('SSAA not implemented');
    }

    // Заглушки для остальных методов интерфейса iApp
    assignClippingPlanes(scene: Scene) {}
    disablePostprocessing(keepOutline: boolean, keepGTAO: boolean) {}
    dispose() {
        this.renderer.dispose();
    }
    disposeEnvironment() {}
    enablePostprocessing(effects: Array<any>) {}
    endWebXR() {}
    generateRTargetPMREM(cubeRT: any) { return cubeRT; }
    initPostprocessing() {}
    initWebXR() {}
    pause() { this.enableRender = false; }
    resume() { this.enableRender = true; }
    printPerformanceInfo(delta: number) {}
    setFrameRateDivider(divider: number) { this.frameRateDivider = divider; }
    unload(rootObj?: any) {}
    updateConstraints(scene: Scene) {}
    updateEnvironment(wMat: any) {}
    updateReflectionProbes(object3d: any) {}
}
